/**
 * Document Preview - JavaScript Interop for Blazor
 * 
 * Provides blob URL creation and document rendering for preview modal.
 * Usage in C#: await JSRuntime.InvokeAsync("documentPreview.functionName", args);
 * 
 * docx-preview, xlsx and pdfjs-dist are loaded on demand to keep the main
 * bundle small.
 *
 * Caching: downloaded content is cached by document GUID to avoid redundant
 * backend calls when the same document is previewed multiple times.
 */

import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Cache: documentGuid -> { blobUrl, arrayBuffer, contentType }
const cache = new Map();
const MAX_CACHE_SIZE = 20;

function evictOldestIfNeeded() {
    if (cache.size >= MAX_CACHE_SIZE) {
        const oldest = cache.keys().next().value;
        const entry = cache.get(oldest);
        if (entry.blobUrl) {
            URL.revokeObjectURL(entry.blobUrl);
        }
        cache.delete(oldest);
    }
}

/**
 * Checks if a document is already cached.
 * @param {string} documentGuid
 * @returns {boolean}
 */
function isCached(documentGuid) {
    return cache.has(documentGuid);
}

/**
 * Returns the cached blob URL for a document, or null if not cached.
 * @param {string} documentGuid
 * @returns {string|null}
 */
function getCachedBlobUrl(documentGuid) {
    return cache.get(documentGuid)?.blobUrl ?? null;
}

/**
 * Creates a blob URL from a Blazor DotNetStreamReference and caches it.
 * @param {DotNetStreamReference} streamReference 
 * @param {string} contentType 
 * @param {string} documentGuid
 * @returns {string} The blob URL
 */
async function createBlobUrl(streamReference, contentType, documentGuid) {
    if (documentGuid && cache.has(documentGuid)) {
        return cache.get(documentGuid).blobUrl;
    }

    const arrayBuffer = await streamReference.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);

    if (documentGuid) {
        evictOldestIfNeeded();
        cache.set(documentGuid, { blobUrl, arrayBuffer, contentType });
    }

    return blobUrl;
}

/**
 * Revokes a previously created blob URL to free memory.
 * Only use this for non-cached URLs; cached URLs are managed by the cache.
 * @param {string} url 
 */
function revokeBlobUrl(url) {
    if (url) {
        // Don't revoke if it's in the cache
        for (const entry of cache.values()) {
            if (entry.blobUrl === url) return;
        }
        URL.revokeObjectURL(url);
    }
}

/**
 * Clears all cached entries and revokes their blob URLs.
 */
function clearCache() {
    for (const entry of cache.values()) {
        if (entry.blobUrl) {
            URL.revokeObjectURL(entry.blobUrl);
        }
    }
    cache.clear();
}

/**
 * Caches text content for a document.
 * @param {string} documentGuid
 * @param {string} text
 */
function cacheText(documentGuid, text) {
    if (documentGuid) {
        evictOldestIfNeeded();
        cache.set(documentGuid, { blobUrl: null, arrayBuffer: null, contentType: 'text/plain', text });
    }
}

/**
 * Returns cached text content, or null if not cached.
 * @param {string} documentGuid
 * @returns {string|null}
 */
function getCachedText(documentGuid) {
    return cache.get(documentGuid)?.text ?? null;
}

/**
 * Renders a DOCX file with styling preserved inside the specified element.
 * @param {DotNetStreamReference|null} streamReference - null if loading from cache
 * @param {string} elementId 
 * @param {string} documentGuid
 */
async function renderDocx(streamReference, elementId, documentGuid) {
    const element = document.getElementById(elementId);
    if (!element) return;

    let arrayBuffer;
    if (documentGuid && cache.has(documentGuid)) {
        arrayBuffer = cache.get(documentGuid).arrayBuffer;
    } else {
        arrayBuffer = await streamReference.arrayBuffer();
        if (documentGuid) {
            evictOldestIfNeeded();
            cache.set(documentGuid, { blobUrl: null, arrayBuffer, contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        }
    }

    const docx = await import('docx-preview');
    await docx.renderAsync(arrayBuffer, element, null, {
        className: 'docx-preview-wrapper',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        breakPages: true,
        ignoreLastRenderedPageBreak: true,
        experimental: false,
        trimXmlDeclaration: true,
        useBase64URL: true,
    });
}

// Base URL of the pdf.js data files copied into wwwroot by copy-pdfjs-assets.js.
const PDFJS_ASSET_BASE = '/dist/pdfjs/';

// Pages are rasterised at this multiple of their natural size so that text
// stays legible; devicePixelRatio is applied on top for high-density screens.
const PDF_RENDER_SCALE = 1.5;

/**
 * Width available for content inside an element, excluding its padding.
 * @param {HTMLElement} element
 * @returns {number}
 */
function getContentWidth(element) {
    const style = getComputedStyle(element);
    return element.clientWidth
        - Number.parseFloat(style.paddingLeft)
        - Number.parseFloat(style.paddingRight);
}

/**
 * Renders a PDF as a stack of canvases inside the specified element.
 *
 * The browser's own PDF viewer is deliberately not used: it is an optional
 * component that users can switch off ("always download PDF files"), and when
 * it is off an embedded PDF cannot render at all. Drawing the pages ourselves
 * makes the preview independent of that setting.
 *
 * @param {DotNetStreamReference|null} streamReference - null if loading from cache
 * @param {string} elementId
 * @param {string} documentGuid
 */
async function renderPdf(streamReference, elementId, documentGuid) {
    const element = document.getElementById(elementId);
    if (!element) return;

    let arrayBuffer;
    if (documentGuid && cache.has(documentGuid)) {
        arrayBuffer = cache.get(documentGuid).arrayBuffer;
    } else {
        arrayBuffer = await streamReference.arrayBuffer();
        if (documentGuid) {
            evictOldestIfNeeded();
            cache.set(documentGuid, { blobUrl: null, arrayBuffer, contentType: 'application/pdf' });
        }
    }

    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

    // Keep the loading task: it owns destroy(), the document proxy does not.
    const loadingTask = pdfjs.getDocument({
        // getDocument hands the buffer to the worker and detaches it, which
        // would leave the cached copy empty for the next preview of the same
        // document. Give it a throwaway clone instead.
        data: arrayBuffer.slice(0),
        cMapUrl: `${PDFJS_ASSET_BASE}cmaps/`,
        cMapPacked: true,
        standardFontDataUrl: `${PDFJS_ASSET_BASE}standard_fonts/`,
        wasmUrl: `${PDFJS_ASSET_BASE}wasm/`,
        iccUrl: `${PDFJS_ASSET_BASE}iccs/`
    });

    const pdfDocument = await loadingTask.promise;

    try {
        element.replaceChildren();
        const outputScale = window.devicePixelRatio || 1;
        const availableWidth = getContentWidth(element);

        for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
            // The modal can be closed while later pages are still rendering.
            if (!element.isConnected) return;

            const page = await pdfDocument.getPage(pageNumber);
            // Scale the page down to fit rather than letting CSS shrink it:
            // the text layer positions its runs from the viewport scale, so a
            // canvas resized after the fact would drift out of alignment.
            const naturalWidth = page.getViewport({ scale: 1 }).width;
            const scale = Math.min(PDF_RENDER_SCALE, availableWidth / naturalWidth);
            const viewport = page.getViewport({ scale });

            const pageElement = document.createElement('div');
            pageElement.className = 'document-preview__pdf-page';
            pageElement.style.width = `${Math.floor(viewport.width)}px`;
            pageElement.style.height = `${Math.floor(viewport.height)}px`;
            // Consumed by the .textLayer styles to size the text runs.
            pageElement.style.setProperty('--total-scale-factor', viewport.scale);

            const canvas = document.createElement('canvas');
            canvas.width = Math.floor(viewport.width * outputScale);
            canvas.height = Math.floor(viewport.height * outputScale);
            pageElement.append(canvas);

            const textLayerElement = document.createElement('div');
            textLayerElement.className = 'textLayer';
            pageElement.append(textLayerElement);

            element.append(pageElement);

            await page.render({
                canvas,
                viewport,
                transform: outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0]
            }).promise;

            // Invisible, selectable text positioned over the canvas, so that
            // selecting, copying and the browser's find-in-page keep working.
            const textLayer = new pdfjs.TextLayer({
                textContentSource: page.streamTextContent(),
                container: textLayerElement,
                viewport
            });
            await textLayer.render();
        }
    } finally {
        await loadingTask.destroy();
    }
}

/**
 * Renders an Excel file as an HTML table inside the specified element.
 * @param {DotNetStreamReference|null} streamReference - null if loading from cache
 * @param {string} elementId 
 * @param {string} documentGuid
 */
async function renderXlsx(streamReference, elementId, documentGuid) {
    const element = document.getElementById(elementId);
    if (!element) return;

    let arrayBuffer;
    if (documentGuid && cache.has(documentGuid)) {
        arrayBuffer = cache.get(documentGuid).arrayBuffer;
    } else {
        arrayBuffer = await streamReference.arrayBuffer();
        if (documentGuid) {
            evictOldestIfNeeded();
            cache.set(documentGuid, { blobUrl: null, arrayBuffer, contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        }
    }

    const XLSX = await import('xlsx');
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    let html = '';

    // Render sheet tabs if multiple sheets
    if (workbook.SheetNames.length > 1) {
        html += '<div class="document-preview__sheet-tabs">';
        workbook.SheetNames.forEach((name, index) => {
            html += `<button class="document-preview__sheet-tab ${index === 0 ? 'active' : ''}" data-sheet-index="${index}">${name}</button>`;
        });
        html += '</div>';
    }

    // Render each sheet
    workbook.SheetNames.forEach((name, index) => {
        const sheet = workbook.Sheets[name];
        const sheetHtml = XLSX.utils.sheet_to_html(sheet);
        html += `<div class="document-preview__sheet" data-sheet-index="${index}" style="${index === 0 ? '' : 'display:none'}">${sheetHtml}</div>`;
    });

    element.innerHTML = html;

    // Add tab click handlers
    const tabs = element.querySelectorAll('.document-preview__sheet-tab');
    const sheets = element.querySelectorAll('.document-preview__sheet');
    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            const idx = tab.dataset.sheetIndex;
            tabs.forEach(t => t.classList.remove('active'));
            sheets.forEach(s => s.style.display = 'none');
            tab.classList.add('active');
            element.querySelector(`.document-preview__sheet[data-sheet-index="${idx}"]`).style.display = '';
        });
    });
}

// Expose to window for Blazor JS interop
window.documentPreview = {
    isCached,
    getCachedBlobUrl,
    cacheText,
    getCachedText,
    createBlobUrl,
    revokeBlobUrl,
    clearCache,
    renderDocx,
    renderPdf,
    renderXlsx
};
